import React, { useState, useEffect } from "react";
import { FaEdit, FaTrash } from "react-icons/fa";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import axios from "axios";

const ExhaustSection = ({ isOpen, toggle, type }) => {
  const [formData, setFormData] = useState({
    description: "",
    actual_tag: "",
    datatype: "bool",
    unit: "",
    min_value: "",
    max_value: "",
    response_type_one: 0,
    response_colors_one: "green",
    response_type_two: 1,
    response_colors_two: "green",
    submitted: false,
    type: type,
  });

  const [responseMessage, setResponseMessage] = useState("");

  // Fetch configuration when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchConfig();
    }
  }, [isOpen]);

  const fetchConfig = async () => {
    try {
      // ✅ Fixed type in URL using template literal
      const res = await axios.get(
        `https://api.hanoch.in/equipment/alarms/?type=${type}`
      );

      if (res && res.length > 0) {
        const item = res[0];
        setFormData({
          description: item.description || "",
          actual_tag: item.actual_tag || "",
          datatype: item.datatype || "bool",
          unit: item.unit || "",
          min_value: item.min_value || "",
          max_value: item.max_value || "",
          response_type_one: item.response_type_one ?? 0,
          response_colors_one: item.response_colors_one || "green",
          response_type_two: item.response_type_two ?? 1,
          response_colors_two: item.response_colors_two || "green",
          submitted: true,
          id: item.id,
          type: type,
        });
      } else {
        // If no existing record
        resetForm();
      }
    } catch (err) {
      console.error("Error fetching config:", err);
    }
  };

  const resetForm = () => {
    setFormData({
      description: "",
      actual_tag: "",
      datatype: "bool",
      unit: "",
      min_value: "",
      max_value: "",
      response_type_one: 0,
      response_colors_one: "green",
      response_type_two: 1,
      response_colors_two: "green",
      submitted: false,
      type: type,
    });
  };

  // Handle field change
  const handleParamChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Save data (POST or PUT)
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (formData.id) {
        // Update existing
        await axios.put(
          `https://api.hanoch.in/equipment/alarms/${formData.id}/`,
          formData
        );
        setResponseMessage("Configuration updated successfully ✅");
      } else {
        // Create new
        const res = await axios.post(
          "https://api.hanoch.in/equipment/alarms/",
          formData
        );
        if (res) {
          setFormData((prev) => ({ ...prev, submitted: true, id: res.id }));
          setResponseMessage("Row submitted successfully ✅");
        }
        fetchConfig();
      }
    } catch (err) {
      console.error("Error submitting data:", err);
      setResponseMessage("❌ Error submitting data");
    }
  };

  // Edit existing entry
  const handleEdit = () => {
    setFormData((prev) => ({ ...prev, submitted: false }));
  };

  // Delete entry
  const handleDelete = async () => {
    try {
      if (formData.id) {
        await axios.delete(
          `https://api.hanoch.in/equipment/alarms/${formData.id}/`
        );
        setResponseMessage("Configuration deleted successfully 🗑️");
      }
      resetForm();
    } catch (err) {
      console.error("Error deleting:", err);
      setResponseMessage("❌ Error deleting data");
    }
  };

  // Auto clear message after 3 seconds
  useEffect(() => {
    if (responseMessage) {
      const timer = setTimeout(() => setResponseMessage(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [responseMessage]);

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="xl">
      <ModalHeader className="bg-light p-3" toggle={toggle}>
        Configuration (Type {type})
      </ModalHeader>

      {responseMessage && (
        <div
          className={`alert ${
            responseMessage.includes("Error")
              ? "alert-danger"
              : "alert-success"
          }`}
        >
          {responseMessage}
        </div>
      )}

      <ModalBody>
        <div className="table-responsive">
          <table className="table align-middle mb-0">
            <thead>
              <tr>
                <th>Description</th>
                <th>Tag</th>
                <th>Datatype</th>
                <th>Response Type 1</th>
                <th>Color 1</th>
                <th>Response Type 2</th>
                <th>Color 2</th>
                <th>Unit</th>
                <th>Min</th>
                <th>Max</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.description}
                    disabled={formData.submitted}
                    onChange={(e) =>
                      handleParamChange("description", e.target.value)
                    }
                  />
                </td>
                <td>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.actual_tag}
                    disabled={formData.submitted}
                    onChange={(e) =>
                      handleParamChange("actual_tag", e.target.value)
                    }
                  />
                </td>
                <td>
                  <select
                    className="form-select"
                    value={formData.datatype}
                    disabled={formData.submitted}
                    onChange={(e) =>
                      handleParamChange("datatype", e.target.value)
                    }
                  >
                    <option value="bool">bool</option>
                    <option value="float">float</option>
                    <option value="int">int</option>
                  </select>
                </td>

                <td>
                  <select
                    className="form-select"
                    value={formData.response_type_one}
                    disabled={formData.submitted}
                    onChange={(e) =>
                      handleParamChange("response_type_one", e.target.value)
                    }
                  >
                    <option value="0">0</option>
                    <option value="1">1</option>
                  </select>
                </td>

                <td>
                  <select
                    className="form-select"
                    value={formData.response_colors_one}
                    disabled={formData.submitted}
                    onChange={(e) =>
                      handleParamChange("response_colors_one", e.target.value)
                    }
                  >
                    <option value="green">🟢 Green</option>
                    <option value="red">🔴 Red</option>
                    <option value="yellow">🟡 Yellow</option>
                    <option value="blue">🔵 Blue</option>
                  </select>
                </td>

                <td>
                  <select
                    className="form-select"
                    value={formData.response_type_two}
                    disabled={formData.submitted}
                    onChange={(e) =>
                      handleParamChange("response_type_two", e.target.value)
                    }
                  >
                    <option value="0">0</option>
                    <option value="1">1</option>
                  </select>
                </td>

                <td>
                  <select
                    className="form-select"
                    value={formData.response_colors_two}
                    disabled={formData.submitted}
                    onChange={(e) =>
                      handleParamChange("response_colors_two", e.target.value)
                    }
                  >
                    <option value="green">🟢 Green</option>
                    <option value="red">🔴 Red</option>
                    <option value="yellow">🟡 Yellow</option>
                    <option value="blue">🔵 Blue</option>
                  </select>
                </td>

                <td>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.unit}
                    disabled={formData.submitted}
                    onChange={(e) => handleParamChange("unit", e.target.value)}
                  />
                </td>

                <td>
                  <input
                    type="number"
                    className="form-control"
                    value={formData.min_value}
                    disabled={formData.submitted}
                    onChange={(e) =>
                      handleParamChange("min_value", e.target.value)
                    }
                  />
                </td>

                <td>
                  <input
                    type="number"
                    className="form-control"
                    value={formData.max_value}
                    disabled={formData.submitted}
                    onChange={(e) =>
                      handleParamChange("max_value", e.target.value)
                    }
                  />
                </td>

                <td>
                  {!formData.submitted ? (
                    <button
                      className="btn btn-success btn-sm"
                      onClick={handleSubmit}
                    >
                      Save
                    </button>
                  ) : (
                    <>
                      <FaEdit
                        className="text-warning me-3"
                        style={{ cursor: "pointer" }}
                        size={18}
                        onClick={handleEdit}
                      />
                      <FaTrash
                        className="text-danger"
                        style={{ cursor: "pointer" }}
                        size={18}
                        onClick={handleDelete}
                      />
                    </>
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </ModalBody>

      <ModalFooter>
        <button type="button" className="btn btn-light" onClick={toggle}>
          Close
        </button>
      </ModalFooter>
    </Modal>
  );
};

export default ExhaustSection;
